import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { signIn } from 'aws-amplify/auth'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import { defaultStorage, sessionStorage } from 'aws-amplify/utils'
import { Field, Form, Formik } from 'formik'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import AWSWAFCaptchaModal from '../components/AWSCaptchaModal'
import Button from '../components/Button'
import FormReturnHandler from '../components/FormReturnHandler'
import Password from '../components/Password'
import Separator from '../components/Separator'
import { signInUser } from '../utils'

export const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const justSignedUp = location?.state?.justSignedUp || false
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [loading, setLoading] = useState(false)

  const authenticate = async ({ email, password, ...rest }: any) => {
    setLoading(true)
    const rememberMe = rest['remember-me']
    try {
      if (rememberMe)
        cognitoUserPoolsTokenProvider.setKeyValueStorage(defaultStorage)
      else cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage)
      const cognitoRes = await signIn({ username: email, password })
      if (!cognitoRes.isSignedIn) {
        if (cognitoRes.nextStep  && cognitoRes.nextStep.signInStep === "CONFIRM_SIGN_UP") {
          throw new Error("Please check your inbox and click the verification link. The email may take a few minutes to arrive.")
        }
        else {
          throw new Error("Unknown error while login in. Please try again or contact support.")
        }
      }
      await signInUser()
      navigate('/')
    } catch (e: any) {
      if (e.name === 'Unknown') {
        window.shouldShowCaptcha = true
        await authenticate({ email, password, ...rest })
      } else {
        console.log(e)
        setErrorMessage(e.message)
        setHasError(true)
        setLoading(false)
      }
    }
  }

  return (
    <>
      {justSignedUp && (
        <FormReturnHandler
          success
          message="An email has been sent to you with a link to activate your account"
          onClose={() => {}}
          open={justSignedUp}
        />
      )}
      <Dialog
        open={hasError}
        onClose={() => setHasError(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="mx-auto max-w-sm rounded-lg p-10 bg-primary-50">
            <DialogTitle>Error while logging in</DialogTitle>
            <Description className="py-4">{errorMessage}</Description>

            <button
              className="ircam-button button button-regular button-dark block mx-auto px-4 py-2"
              type="button"
              onClick={() => setHasError(false)}
            >
              <div className="button-label">OK</div>
            </button>
          </DialogPanel>
        </div>
      </Dialog>
      <div className="w-4/6 pt-4 mx-auto mt-16">
        <h1 className="text-4xl text-center mt-8 mb-4 font-light">Log in</h1>
        <Separator />
        <div>
          <h3 className="text-center my-8">Login with your email address:</h3>
          <Formik
            initialValues={{
              email: '',
              password: '',
              'remember-me': true,
            }}
            onSubmit={authenticate}
          >
            <Form>
              <label htmlFor="email" className="text-sm my-4 block">
                <div className="w-full">Email address</div>
                <Field
                  type="text"
                  id="email"
                  name="email"
                  className="w-full ircam-input"
                />
              </label>
              <label htmlFor="password" className="text-sm my-4 block">
                <div className="w-full">Password</div>
                <Field as={Password} name="password" id="password" />
              </label>
              <div className="my-4">
                <label htmlFor="remember-me" className="text-sm">
                  <Field
                    type="checkbox"
                    id="remember-me"
                    name="remember-me"
                    className="rounded mr-2"
                  />
                  Remember me ?
                </label>
              </div>
              <div className="w-full my-4">
                <button
                  type="submit"
                  className="w-full button button-large button-dark py-2 px-4"
                  disabled={loading}
                >
                  <div className="button button-label">
                    {loading ? 'Loading...' : 'Sign In'}
                  </div>
                </button>
                <Link
                  className="block w-full text-center my-2 text-sm hover:underline"
                  to="/forgot-password"
                >
                  Forgot your password ?
                </Link>
                <Link
                  className="block w-full text-center my-2 text-sm hover:underline"
                  to="/resend-activation-link"
                >
                  Send me another activation email
                </Link>
              </div>
            </Form>
          </Formik>
          <Separator />
          <div className="text-center">
            <h3 className="text-sm text-primary-500 my-4">
              You don&lsquo;t have any account ?
            </h3>
            <Button
              as={Link}
              to="/signup"
              label="Sign up"
              dark={false}
              className="button w-full py-2 text-center"
            />
          </div>
        </div>
      </div>
      <AWSWAFCaptchaModal />
    </>
  )
}
